/**
 * Measure whether an attribute blocks vertically or horizontally on the merchant's own
 * wall. Verticality = column stability (each value holds one x-interval top to bottom)
 * x x-exclusivity (sibling values tile x rather than share it); either term alone
 * crowns a near-constant. Measured per scope, and a scope the attribute does not split
 * does not vote. Input must be the base pog, never a layout this pipeline produced.
 */
import type { StudioItem, StudioRunGeometry } from "./studioTypes";
import { isPlaceholderValue } from "./studioTypes";
import { bayShelfOf } from "@CommonUtils/cdtStudio/domain/wallRows";
import { compactRowsOf } from "./shelfRankSpace";

/** At or above this an attribute is columnar, at or below it banded. Between the
 *  two the evidence does not decide and a human has to — see `warning`. */
export const VERTICAL_SCORE = 0.5;
export const HORIZONTAL_SCORE = 0.3;

export type BlockRole = "vertical" | "horizontal" | "ambiguous";

export interface ValueBlocking {
  value: string;
  /** Mean adjacent-row x-interval overlap, 0-1. 1 = the interval never moves. */
  stability: number;
  /** Share of the scope's shelves the value reaches, 0-1. */
  coverage: number;
  /** Total placed width, cm — this value's weight in the roll-up. */
  widthCm: number;
  rows: number;
}

export interface ScopeBlocking {
  run: number;
  path: readonly string[];
  /** 1 = sibling values tile x, 0 = they sit on top of each other. */
  exclusivity: number;
  /** Width-weighted mean of the values' column stability. */
  stability: number;
  /** exclusivity x stability. */
  score: number;
  widthCm: number;
  values: readonly ValueBlocking[];
}

export interface BlockingReading {
  attr: string;
  role: BlockRole;
  /** Width-weighted mean of the scopes' scores. */
  score: number;
  /** Spread of the scope scores; a split attribute is a ruling, not a default. */
  disagreement: number;
  /** Scopes the attribute actually splits. None = inert, and it holds no role. */
  scopes: readonly ScopeBlocking[];
  values: readonly ValueBlocking[];
  warning?: string;
}

interface Interval {
  min: number;
  max: number;
}

const spanOverlap = (a: Interval, b: Interval): number =>
  Math.max(0, Math.min(a.max, b.max) - Math.max(a.min, b.min));

const intervalsByRow = (
  placements: readonly { shelf: number; xCm: number; widthCm: number }[],
): Map<number, Interval> => {
  const rows = new Map<number, Interval>();
  for (const placement of placements) {
    const row = rows.get(placement.shelf);
    const min = placement.xCm;
    const max = placement.xCm + placement.widthCm;
    if (row) {
      row.min = Math.min(row.min, min);
      row.max = Math.max(row.max, max);
    } else rows.set(placement.shelf, { min, max });
  }
  return rows;
};

/** Mean row width of a value: the denominator for how much x-stripe it holds. */
const meanRowWidth = (rows: ReadonlyMap<number, Interval>): number => {
  let total = 0;
  for (const row of rows.values()) total += row.max - row.min;
  return rows.size ? total / rows.size : 0;
};

const spanOf = (rows: ReadonlyMap<number, Interval>): Interval => {
  const span: Interval = { min: Infinity, max: -Infinity };
  for (const row of rows.values()) {
    span.min = Math.min(span.min, row.min);
    span.max = Math.max(span.max, row.max);
  }
  return span;
};

/** The items under one parent path, on one wall. */
export interface BlockingScope {
  run: number;
  path: readonly string[];
  items: readonly StudioItem[];
}

const measureScope = (
  scope: BlockingScope,
  attr: string,
  run: StudioRunGeometry,
): ScopeBlocking | null => {
  const ranks = [...run.shelves].map((shelf) => shelf.rank).sort((a, b) => a - b);
  const byValue = new Map<string, { shelf: number; xCm: number; widthCm: number }[]>();
  for (const item of scope.items) {
    const value = String(item.attrs[attr] ?? "");
    if (isPlaceholderValue(value)) continue;
    for (const placement of item.placements) {
      if (placement.run !== run.run) continue;
      let bucket = byValue.get(value);
      if (!bucket) {
        bucket = [];
        byValue.set(value, bucket);
      }
      bucket.push(placement);
    }
  }
  if (byValue.size < 2) return null;

  // Coverage is against the scope's own rows, not the whole wall.
  const scopeRows = new Set<number>();
  for (const placements of byValue.values())
    for (const placement of placements) scopeRows.add(placement.shelf);
  const scopeRanks = ranks.filter((rank) => scopeRows.has(rank));

  const values: ValueBlocking[] = [];
  const shapes: { rows: Map<number, Interval>; widthCm: number }[] = [];
  for (const [value, placements] of byValue) {
    const rows = intervalsByRow(placements);
    const widthCm = placements.reduce((total, placement) => total + placement.widthCm, 0);
    // Adjacent rows of the scope, not of the value: a skipped shelf must count against it.
    let pairs = 0;
    let overlapTotal = 0;
    for (let index = 0; index + 1 < scopeRanks.length; index += 1) {
      const above = rows.get(scopeRanks[index]);
      const below = rows.get(scopeRanks[index + 1]);
      if (!above && !below) continue;
      pairs += 1;
      if (!above || !below) continue;
      const smaller = Math.min(above.max - above.min, below.max - below.min);
      overlapTotal += smaller > 0 ? Math.min(1, spanOverlap(above, below) / smaller) : 1;
    }
    values.push({
      value,
      stability: pairs > 0 ? overlapTotal / pairs : 0,
      coverage: scopeRanks.length ? rows.size / scopeRanks.length : 0,
      widthCm,
      rows: rows.size,
    });
    shapes.push({ rows, widthCm });
  }

  // Exclusivity: how much of the narrower sibling's x-stripe the other one takes.
  let shared = 0;
  let paired = 0;
  for (let i = 0; i < shapes.length; i += 1) {
    for (let j = i + 1; j < shapes.length; j += 1) {
      const a = shapes[i];
      const b = shapes[j];
      const smaller = Math.min(meanRowWidth(a.rows), meanRowWidth(b.rows));
      if (!(smaller > 0)) continue;
      // Width-weighted, or a long tail of slivers outvotes the blocks.
      const weight = Math.min(a.widthCm, b.widthCm);
      shared += weight * Math.min(1, spanOverlap(spanOf(a.rows), spanOf(b.rows)) / smaller);
      paired += weight;
    }
  }
  const exclusivity = paired > 0 ? 1 - shared / paired : 1;

  const voting = values.filter((value) => value.rows > 1);
  const weight = voting.reduce((total, value) => total + value.widthCm, 0);
  const stability = weight
    ? voting.reduce((total, value) => total + value.widthCm * value.stability, 0) / weight
    : 0;
  return {
    run: run.run,
    path: scope.path,
    exclusivity: +exclusivity.toFixed(3),
    stability: +stability.toFixed(3),
    score: +(exclusivity * stability).toFixed(3),
    widthCm: values.reduce((total, value) => total + value.widthCm, 0),
    values: [...values].sort((a, b) => b.widthCm - a.widthCm),
  };
};

/** Split a set of scopes by an attribute — how the caller descends the tree. */
export const splitBlockingScopes = (
  scopes: readonly BlockingScope[],
  attr: string,
): BlockingScope[] =>
  scopes.flatMap((scope) => {
    const groups = new Map<string, StudioItem[]>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    return [...groups.entries()].map(([value, items]) => ({
      run: scope.run,
      path: [...scope.path, value],
      items,
    }));
  });

/** One scope per run represented by an item's placements. Placeless items fall back to
 *  their source run so a caller with no geometry still gets a scope. */
export const rootBlockingScopes = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): BlockingScope[] => {
  const byRun = new Map<number, StudioItem[]>();
  for (const item of items) {
    const itemRuns = new Set(item.placements.map((placement) => placement.run));
    if (itemRuns.size === 0) itemRuns.add(item.run);
    for (const itemRun of itemRuns) {
      let bucket = byRun.get(itemRun);
      if (!bucket) {
        bucket = [];
        byRun.set(itemRun, bucket);
      }
      bucket.push(item);
    }
  }
  for (const run of runs) if (!byRun.has(run.run)) byRun.set(run.run, []);
  return [...byRun.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([run, scopeItems]) => ({ run, path: [], items: scopeItems }));
};

export const measureBlockingOf = (
  items: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
  scopes: readonly BlockingScope[] = rootBlockingScopes(items, runs),
): BlockingReading => {
  const byRun = new Map(runs.map((run) => [run.run, run]));
  const measured: ScopeBlocking[] = [];
  for (const scope of scopes) {
    const run = byRun.get(scope.run);
    if (!run) continue;
    const reading = measureScope(scope, attr, run);
    if (reading) measured.push(reading);
  }
  const weight = measured.reduce((total, scope) => total + scope.widthCm, 0);
  const score = weight
    ? measured.reduce((total, scope) => total + scope.widthCm * scope.score, 0) / weight
    : 0;
  const mean = measured.length
    ? measured.reduce((total, scope) => total + scope.score, 0) / measured.length
    : 0;
  const disagreement = measured.length
    ? Math.sqrt(
        measured.reduce((total, scope) => total + (scope.score - mean) ** 2, 0) / measured.length,
      )
    : 0;
  const role: BlockRole =
    measured.length === 0
      ? "ambiguous"
      : score >= VERTICAL_SCORE
        ? "vertical"
        : score <= HORIZONTAL_SCORE
          ? "horizontal"
          : "ambiguous";
  return {
    attr,
    role,
    score: +score.toFixed(3),
    disagreement: +disagreement.toFixed(3),
    scopes: measured,
    values: measured.flatMap((scope) => scope.values),
    warning:
      role === "ambiguous"
        ? measured.length === 0
          ? `"${attr}" splits no scope of the base pog — it blocks neither vertically nor horizontally and must not spend a level.`
          : `"${attr}" scores ${score.toFixed(2)}, between banded (${HORIZONTAL_SCORE}) and columnar (${VERTICAL_SCORE}). A wrong default here inverts the level roles; rule on it.`
        : undefined,
  };
};

/** Bandedness places cdt2: not-columnar does not mean banded (an attribute can merely
 *  sequence packs along the shelf), so cdt2 takes the attribute whose values own
 *  disjoint runs of shelves inside their parent's column. */
export interface BandednessReading {
  attr: string;
  /** Width-weighted share of sibling pairs holding disjoint shelf sets, 0-1. */
  score: number;
  /** Scopes the attribute splits. */
  scopes: number;
  /** Mean run-length of a value's shelves; 1 = it never spans a band. */
  contiguity: number;
}

const shelvesOf = (
  items: readonly StudioItem[],
  run: number,
  ordinalOf: (rank: number) => number,
): Set<number> => {
  const shelves = new Set<number>();
  for (const item of items)
    for (const placement of item.placements)
      if (placement.run === run) shelves.add(ordinalOf(placement.shelf));
  return shelves;
};

export const measureBandednessOf = (
  items: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
  scopes: readonly BlockingScope[] = rootBlockingScopes(items, runs),
): BandednessReading => {
  // Shelf runs are counted on the compact rows: unused hole rows do not cut a band in two.
  const compact = compactRowsOf(runs, items);
  const { ordinalOf } = compact;
  let disjoint = 0;
  let paired = 0;
  let contiguous = 0;
  let counted = 0;
  let splitScopes = 0;
  for (const scope of scopes) {
    const groups = new Map<string, StudioItem[]>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      if (isPlaceholderValue(value)) continue;
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    if (groups.size < 2) continue;
    splitScopes += 1;
    const runGeometry = compact.runs.find((run) => run.run === scope.run);
    const shapes = [...groups.values()].map((group) => ({
      shelves: shelvesOf(group, scope.run, ordinalOf),
      /* Counted on each bay's own boards: on a ragged wall a wall row the bay does not carry
         is not a gap between its shelves. */
      bayShelves: new Set(
        group.flatMap((item) =>
          item.placements
            .filter((placement) => placement.run === scope.run)
            .map((placement) => bayShelfOf(runGeometry, placement.bay, ordinalOf(placement.shelf))),
        ),
      ),
      widthCm: group.reduce(
        (total, item) =>
          total +
          item.placements
            .filter((placement) => placement.run === scope.run)
            .reduce((width, placement) => width + placement.widthCm, 0),
        0,
      ),
    }));
    for (const shape of shapes) {
      if (shape.shelves.size === 0) continue;
      // One contiguous run of shelves = one band. Two runs = a split block.
      const ranks = [...shape.bayShelves].sort((a, b) => a - b);
      let runsOfShelves = 1;
      for (let index = 1; index < ranks.length; index += 1)
        if (ranks[index] !== ranks[index - 1] + 1) runsOfShelves += 1;
      contiguous += shape.widthCm * (1 / runsOfShelves);
      counted += shape.widthCm;
    }
    for (let i = 0; i < shapes.length; i += 1) {
      for (let j = i + 1; j < shapes.length; j += 1) {
        const a = shapes[i];
        const b = shapes[j];
        const smaller = Math.min(a.shelves.size, b.shelves.size);
        if (smaller === 0) continue;
        const shared = [...a.shelves].filter((shelf) => b.shelves.has(shelf)).length;
        const weight = Math.min(a.widthCm, b.widthCm);
        disjoint += weight * (1 - shared / smaller);
        paired += weight;
      }
    }
  }
  return {
    attr,
    score: paired > 0 ? +(disjoint / paired).toFixed(3) : 0,
    scopes: splitScopes,
    contiguity: counted > 0 ? +(contiguous / counted).toFixed(3) : 0,
  };
};

/** Side by side across a brand block: each value one x-stretch per shelf, in the same left-to-right
 *  order on every shelf, the stretches lined up shelf to shelf. Read per shelf, so a value that
 *  fills one shelf and shares the next (Medium beside Small, then Medium alone) still reads
 *  across: `measureBlockingOf` compares whole-scope spans and calls that 0. */
export interface AcrossReading {
  attr: string;
  /** exclusivity x order x alignment x mixed. */
  score: number;
  /** 1 = values never overlap in x on a shelf they share. */
  exclusivity: number;
  /** 1 = every pair of values keeps one left-to-right order on every shelf they share. */
  order: number;
  /** 1 = each value's stretch on a shelf overlaps its stretch on the next shelf fully. */
  alignment: number;
  /** Width share on shelves where two or more values stand side by side. A value alone on its
   *  shelves is a row, not a block across. */
  mixed: number;
}

/** One shelf of a scope cut into stretches: a gap wide enough to hold the narrower of the two
 *  placements beside it means something else stands there (the brand block steps sideways,
 *  another brand sits between), so the two sides are read as separate stretches. */
const stretchesOf = (
  placements: readonly { value: string; xCm: number; widthCm: number }[],
): { value: string; xCm: number; widthCm: number }[][] => {
  const sorted = [...placements].sort((a, b) => a.xCm - b.xCm);
  const out: { value: string; xCm: number; widthCm: number }[][] = [];
  for (const placement of sorted) {
    const current = out[out.length - 1];
    const last = current?.[current.length - 1];
    const gap = last ? placement.xCm - (last.xCm + last.widthCm) : 0;
    if (!current || gap >= Math.min(last.widthCm, placement.widthCm)) out.push([placement]);
    else current.push(placement);
  }
  return out;
};

export const measureAcrossOf = (
  items: readonly StudioItem[],
  attr: string,
  scopes: readonly BlockingScope[],
): AcrossReading => {
  let exclusiveWeight = 0;
  let sharedWeight = 0;
  let consistent = 0;
  let observed = 0;
  let aligned = 0;
  let stacked = 0;
  let mixedWidth = 0;
  let totalWidth = 0;
  for (const scope of scopes) {
    const byShelf = new Map<number, { value: string; xCm: number; widthCm: number }[]>();
    const values = new Set<string>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      if (isPlaceholderValue(value)) continue;
      values.add(value);
      for (const placement of item.placements) {
        if (placement.run !== scope.run) continue;
        byShelf.set(placement.shelf, [
          ...(byShelf.get(placement.shelf) ?? []),
          { value, xCm: placement.xCm, widthCm: placement.widthCm },
        ]);
      }
    }
    if (values.size < 2) continue;
    const shelves = [...byShelf.keys()].sort((a, b) => a - b);
    // Per shelf, per stretch: each value's x-interval and width.
    const cells = new Map<number, Map<string, { interval: Interval; width: number }>[]>();
    const signs = new Map<string, number[]>();
    for (const shelf of shelves) {
      const stretches = stretchesOf(byShelf.get(shelf) ?? []).map((stretch) => {
        const cell = new Map<string, { interval: Interval; width: number }>();
        for (const { value, xCm, widthCm } of stretch) {
          const entry = cell.get(value);
          if (entry) {
            entry.interval.min = Math.min(entry.interval.min, xCm);
            entry.interval.max = Math.max(entry.interval.max, xCm + widthCm);
            entry.width += widthCm;
          } else cell.set(value, { interval: { min: xCm, max: xCm + widthCm }, width: widthCm });
        }
        return cell;
      });
      cells.set(shelf, stretches);
      for (const cell of stretches) {
        const here = [...cell];
        const width = here.reduce((sum, [, entry]) => sum + entry.width, 0);
        totalWidth += width;
        if (here.length > 1) mixedWidth += width;
        for (let i = 0; i < here.length; i += 1)
          for (let j = i + 1; j < here.length; j += 1) {
            const [va, a] = here[i];
            const [vb, b] = here[j];
            const smaller = Math.min(
              a.interval.max - a.interval.min,
              b.interval.max - b.interval.min,
            );
            const weight = Math.min(a.width, b.width);
            exclusiveWeight += weight;
            if (smaller > 0)
              sharedWeight += weight * Math.min(1, spanOverlap(a.interval, b.interval) / smaller);
            const [first, second, sign] = va < vb ? [va, vb, 1] : [vb, va, -1];
            const leftFirst = a.interval.min + a.interval.max < b.interval.min + b.interval.max;
            signs.set(`${first}␟${second}`, [
              ...(signs.get(`${first}␟${second}`) ?? []),
              leftFirst ? sign : -sign,
            ]);
          }
      }
    }
    for (const list of signs.values()) {
      const left = list.filter((sign) => sign > 0).length;
      consistent += Math.max(left, list.length - left);
      observed += list.length;
    }
    // Lined up: each value's stretch against its best match on the next shelf.
    for (let index = 0; index + 1 < shelves.length; index += 1) {
      const upper = cells.get(shelves[index]) ?? [];
      const lower = cells.get(shelves[index + 1]) ?? [];
      for (const cell of upper)
        for (const [value, entry] of cell) {
          const matches = lower.flatMap((other) => (other.has(value) ? [other.get(value)!] : []));
          if (matches.length === 0) continue;
          stacked += 1;
          aligned += Math.max(
            ...matches.map((match) => {
              const smaller = Math.min(
                entry.interval.max - entry.interval.min,
                match.interval.max - match.interval.min,
              );
              return smaller > 0
                ? Math.min(1, spanOverlap(entry.interval, match.interval) / smaller)
                : 1;
            }),
          );
        }
    }
  }
  const exclusivity = exclusiveWeight > 0 ? 1 - sharedWeight / exclusiveWeight : 0;
  const order = observed > 0 ? consistent / observed : 0;
  const alignment = stacked > 0 ? aligned / stacked : 1;
  const mixed = totalWidth > 0 ? mixedWidth / totalWidth : 0;
  const round = (n: number) => +n.toFixed(3);
  return {
    attr,
    score: round(exclusivity * order * alignment * mixed),
    exclusivity: round(exclusivity),
    order: round(order),
    alignment: round(alignment),
    mixed: round(mixed),
  };
};

/** A level read as shelf rows stacked top to bottom: each value holds one run of shelves, the
 *  runs come in the level's order by their centres, and a shelf row may share its edge shelves
 *  with the next one (Medium on shelves 2-6 between Small on 1-2 and Large on 5-6 is the middle
 *  shelf row). No disjointness is asked for. */
export interface ShelfRowReading {
  attr: string;
  /** inside x order x oneRun. */
  score: number;
  /** Share of placements on a shelf inside their value's main run of shelves. */
  inside: number;
  /** Share of value pairs whose run centres stand at different heights in the level's order
   *  top to bottom. */
  order: number;
  /** Share of values that stand on one run of shelves. */
  oneRun: number;
  /** Share of value pairs centred on the same shelves: 1 = no value stands higher than another
   *  (side by side on one shelf, or full-height columns). */
  level: number;
  /** Share of value pairs the level ranks that stand upside down (the later value centred
   *  higher): 0 = no pair is out of order, though some may stand level. */
  inverted: number;
}

export const measureShelfRowsOf = (
  items: readonly StudioItem[],
  attr: string,
  /** The level's reading order; values it does not rank keep any order. */
  rankOf: (value: string) => number | undefined,
  scopes: readonly BlockingScope[],
): ShelfRowReading => {
  let inside = 0;
  let placed = 0;
  let kept = 0;
  let pairs = 0;
  let even = 0;
  let upsideDown = 0;
  let single = 0;
  let valuesSeen = 0;
  for (const scope of scopes) {
    const shelvesOf = new Map<string, number[]>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      if (isPlaceholderValue(value)) continue;
      for (const placement of item.placements)
        if (placement.run === scope.run)
          shelvesOf.set(value, [...(shelvesOf.get(value) ?? []), placement.shelf]);
    }
    if (shelvesOf.size < 2) continue;
    const centres: { value: string; centre: number }[] = [];
    for (const [value, shelves] of shelvesOf) {
      const ranks = [...new Set(shelves)].sort((a, b) => a - b);
      const runs: number[][] = [];
      for (const rank of ranks) {
        const last = runs[runs.length - 1];
        if (last && rank === last[last.length - 1] + 1) last.push(rank);
        else runs.push([rank]);
      }
      const count = (run: number[]) => shelves.filter((shelf) => run.includes(shelf)).length;
      const main = runs.reduce((best, run) => (count(run) > count(best) ? run : best), runs[0]);
      inside += count(main);
      placed += shelves.length;
      valuesSeen += 1;
      if (runs.length === 1) single += 1;
      centres.push({ value, centre: (main[0] + main[main.length - 1]) / 2 });
    }
    for (let i = 0; i < centres.length; i += 1)
      for (let j = i + 1; j < centres.length; j += 1) {
        // Shelf rows sit at different heights: two values centred on the same shelves are not
        // stacked at all. Where the level ranks both, the upper one must come first.
        const ra = rankOf(centres[i].value);
        const rb = rankOf(centres[j].value);
        pairs += 1;
        if (centres[i].centre === centres[j].centre) {
          even += 1;
          continue;
        }
        if (ra === undefined || rb === undefined || ra === rb) {
          kept += 1;
          continue;
        }
        const [upper, lower] = ra < rb ? [centres[i], centres[j]] : [centres[j], centres[i]];
        if (upper.centre < lower.centre) kept += 1;
        else upsideDown += 1;
      }
  }
  const share = (n: number, d: number, none: number) => (d > 0 ? n / d : none);
  const insideShare = share(inside, placed, 0);
  const order = share(kept, pairs, 1);
  const oneRun = share(single, valuesSeen, 0);
  const round = (n: number) => +n.toFixed(3);
  return {
    attr,
    score: round(insideShare * order * oneRun),
    inside: round(insideShare),
    order: round(order),
    oneRun: round(oneRun),
    level: round(share(even, pairs, 0)),
    inverted: round(share(upsideDown, pairs, 0)),
  };
};
